import { Switch as HeadlessSwitch } from "@headlessui/react";
import * as React from "react";
import { cn } from "@app/lib/utils";

const switchPrimitiveSizes = {
  sm: {
    root: "h-4 w-8",
    thumb: "h-3 w-3",
    checked: "ltr:translate-x-[18px] rtl:-translate-x-[18px]",
    unchecked: "ltr:translate-x-0.5 rtl:-translate-x-0.5",
  },
  md: {
    root: "h-6 w-11",
    thumb: "h-5 w-5",
    checked: "ltr:translate-x-[22px] rtl:-translate-x-[22px]",
    unchecked: "ltr:translate-x-0.5 rtl:-translate-x-0.5",
  },
  section: {
    root: "h-6 w-11",
    thumb: "h-5 w-5",
    checked: "ltr:translate-x-[22px] rtl:-translate-x-[22px]",
    unchecked: "ltr:translate-x-0.5 rtl:-translate-x-0.5",
  },
} as const;

type HeadlessSwitchProps = React.ComponentProps<typeof HeadlessSwitch>;

export type SwitchPrimitiveProps = Omit<
  HeadlessSwitchProps,
  "className" | "children"
> & {
  activeClassName?: string;
  children?: React.ReactNode;
  className?: string;
  inactiveClassName?: string;
  size?: keyof typeof switchPrimitiveSizes;
  thumbClassName?: string;
};

export function SwitchPrimitive({
  activeClassName = "bg-primary",
  checked,
  children,
  className,
  inactiveClassName = "bg-muted-foreground/45 hover:bg-muted-foreground/55",
  size = "md",
  thumbClassName,
  ...props
}: SwitchPrimitiveProps) {
  const isChecked = Boolean(checked);
  const sizeClassName = switchPrimitiveSizes[size];

  return (
    <HeadlessSwitch
      checked={checked}
      className={cn(
        "relative inline-flex shrink-0 items-center rounded-full transition-colors after:absolute after:-inset-3 after:content-[''] focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:cursor-not-allowed disabled:opacity-100 disabled:saturate-75 md:after:-inset-2",
        sizeClassName.root,
        isChecked ? activeClassName : inactiveClassName,
        className,
      )}
      {...props}
    >
      {children}
      <span
        className={cn(
          "inline-block transform rounded-full bg-background shadow-none transition-transform will-change-transform",
          sizeClassName.thumb,
          isChecked ? sizeClassName.checked : sizeClassName.unchecked,
          thumbClassName,
        )}
      />
    </HeadlessSwitch>
  );
}
